import type { ProducerStanding, ProducerSummary } from "@corklog/shared";
import { type ReactNode, useCallback, useId, useState } from "react";
import { useTranslation } from "react-i18next";

import { Link, useParams } from "react-router";

import { fetchProducers, updateProducerStanding } from "../api.js";
import { reportForProducer, useAreaIndex } from "../area-links.js";
import { useCellar, useMayWrite } from "../cellar.js";
import { CellarHeading } from "../components/CellarHeading.js";
import { Select } from "../components/controls.js";
import { useReload } from "../offline.js";
import { useFeatures } from "../session.js";

/**
 * The producers this cellar holds, and what its owner makes of each.
 *
 * A judgement about a house — not about a bottle, which is a tasting note, and
 * not about a wine, which is a rating. Unknown is where every producer starts,
 * because a house nobody has judged yet is not a house judged poorly.
 *
 * Rows and hairlines, and the count of wines beside each name, because the
 * producer you have twelve wines from is the one whose standing you actually
 * want an opinion on.
 */
const STANDINGS: readonly ProducerStanding[] = ["Renowned", "Good", "Poor", "Unknown"];

type State =
  | { status: "loading" }
  | { status: "ready"; producers: ProducerSummary[] }
  | { status: "failed" };

export function Producers() {
  const { t } = useTranslation();
  const { cellarId = "" } = useParams();
  const [state, setState] = useState<State>({ status: "loading" });
  const [failed, setFailed] = useState<string | null>(null);
  // The standing being looked at, or null for every producer. Local rather than
  // in the address, unlike the wine list's narrowings: nobody links to a list
  // of the houses they think poorly of, and the list is one screen's worth of
  // work — judge the unknown ones — rather than a view worth coming back to.
  const [only, setOnly] = useState<ProducerStanding | null>(null);
  // The houses judged since the narrowing was chosen, which stay on screen
  // whatever they were judged to be. Working through *Okänd* one row at a time
  // is exactly what this filter is for, and a row that vanished the moment its
  // dropdown closed would take the list out from under the thumb and make a
  // slip impossible to take back. They go when the narrowing is changed.
  const [kept, setKept] = useState<ReadonlySet<string>>(new Set());
  const narrow = (standing: ProducerStanding | null) => {
    setOnly(standing);
    setKept(new Set());
  };
  const mayWrite = useMayWrite();
  const isOwner = useCellar()?.role === "Owner";
  // The quality report's link goes with the administrator's switch, since
  // 2026-09-28. The standings it proposed and were taken stay: they are the
  // producers' own now.
  const { quality } = useFeatures();
  const areaIndex = useAreaIndex();

  const load = useCallback(() => {
    void fetchProducers(cellarId)
      .then((producers) => setState({ status: "ready", producers }))
      .catch(() => setState({ status: "failed" }));
  }, [cellarId]);

  useReload(load);

  const change = (producer: ProducerSummary, standing: ProducerStanding) => {
    // Shown immediately and corrected if the write fails: judging a producer is
    // a small act and should not feel like filing a form.
    setState((current) =>
      current.status === "ready"
        ? {
            status: "ready",
            producers: current.producers.map((row) =>
              row.id === producer.id ? { ...row, standing } : row,
            ),
          }
        : current,
    );
    setKept((current) => new Set(current).add(producer.id));
    setFailed(null);
    void updateProducerStanding(cellarId, producer.id, standing).catch(() => {
      setFailed(producer.name);
      load();
    });
  };

  return (
    <section>
      <CellarHeading cellarId={cellarId} current="producers" />
      <p className="text-muted mt-2 max-w-prose text-sm">{t("producers.intro")}</p>

      {/* The way to the six lists of words, from the one anybody comes looking
          for first. It is not in the row of sections: that row already wraps
          onto three lines on a phone. */}
      {mayWrite === true && (
        <p className="mt-3">
          <Link
            to={`/kallare/${cellarId}/ord`}
            className="text-accent text-sm underline-offset-4 hover:underline"
          >
            {t("vocabulary.fromProducers")}
          </Link>
          {/* The quality report proposes a standing for every producer here,
              so the way to it starts on the page that sets them. The owner's
              alone, asked by the role itself rather than by `mayWrite` — see
              `routes/quality.ts`. */}
          {isOwner && quality && (
            <Link
              to={`/kallare/${cellarId}/kvalitet`}
              className="text-accent ml-4 text-sm underline-offset-4 hover:underline"
            >
              {t("quality.fromProducers")}
            </Link>
          )}
        </p>
      )}

      {state.status === "loading" && (
        <p className="text-muted mt-6 text-sm">{t("loading")}</p>
      )}
      {state.status === "failed" && (
        <p role="alert" className="text-past mt-6 text-sm">
          {t("producers.loadFailed")}
        </p>
      )}
      {failed !== null && (
        <p role="alert" className="text-past mt-4 text-sm">
          {t("producers.saveFailed", { name: failed })}
        </p>
      )}

      {state.status === "ready" &&
        (state.producers.length === 0 ? (
          <p className="text-muted mt-8 max-w-prose text-sm">{t("producers.empty")}</p>
        ) : (
          <StandingFilter
            producers={state.producers}
            only={only}
            kept={kept}
            onChange={narrow}
          >
            {(producer) => (
              <li
                key={producer.id}
                className="border-line flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 border-b py-4"
              >
                <div>
                  <span className="font-display text-lg">{producer.name}</span>
                  <span className="text-muted ml-3 text-sm">
                    {t("producers.wineCount", { count: producer.uses })}
                  </span>
                  {/* The area report that names the house, when one is
                      published — 10.36. */}
                  {areaIndex !== null &&
                    (() => {
                      const link = reportForProducer(areaIndex, producer.name);
                      return link === null ? null : (
                        <Link
                          to={`/upptack/${encodeURIComponent(link.id)}`}
                          className="text-accent ml-3 text-sm underline-offset-4 hover:underline"
                        >
                          {t("discover.readAbout", { area: link.area })}
                        </Link>
                      );
                    })()}
                </div>

                {/* The label is beside the control rather than wrapped round
                    it: `Select` draws its own chevron in a positioned box, and
                    a label that contains it would make the whole row a click
                    target for the dropdown.

                    A guest reads the standing and cannot set it. The judgement
                    is worth showing — it is why one producer's wines are
                    bought again and another's are not — so it becomes the word
                    itself rather than a dropdown that would not answer. */}
                {mayWrite === true ? (
                  <div className="w-44">
                    <label className="sr-only" htmlFor={`standing-${producer.id}`}>
                      {t("producers.standingFor", { name: producer.name })}
                    </label>
                    <Select
                      id={`standing-${producer.id}`}
                      value={producer.standing}
                      onChange={(next) => change(producer, next as ProducerStanding)}
                    >
                      {STANDINGS.map((standing) => (
                        <option key={standing} value={standing}>
                          {t(`standing.${standing}`)}
                        </option>
                      ))}
                    </Select>
                  </div>
                ) : (
                  <span className="text-muted text-sm">
                    {t(`standing.${producer.standing}`)}
                  </span>
                )}
              </li>
            )}
          </StandingFilter>
        ))}
    </section>
  );
}

/**
 * The list, narrowed to one standing if one is chosen.
 *
 * One select rather than the wine list's bar, because there is one thing to
 * narrow by. It carries the count of each standing in its options — *Okänd
 * (32)* is the answer to "how much judging is left" before anybody opens it —
 * and all four are offered even at nought, like the window states: the
 * standings are a fixed scale, not a description of what is on the shelves,
 * and *Dålig (0)* is itself worth knowing.
 */
function StandingFilter({
  producers,
  only,
  kept,
  onChange,
  children,
}: {
  producers: readonly ProducerSummary[];
  only: ProducerStanding | null;
  kept: ReadonlySet<string>;
  onChange: (standing: ProducerStanding | null) => void;
  children: (producer: ProducerSummary) => ReactNode;
}) {
  const { t } = useTranslation();
  const id = useId();
  const shown =
    only === null
      ? producers
      : producers.filter(
          (producer) => producer.standing === only || kept.has(producer.id),
        );
  const hiding = shown.length < producers.length;

  return (
    <>
      <div className="mt-8 max-w-xs">
        <label
          className={`block text-sm ${only === null ? "text-muted" : "text-accent"}`}
          htmlFor={id}
        >
          {t("producers.filter.label")}
        </label>
        <div className="mt-1">
          <Select
            id={id}
            value={only ?? ""}
            onChange={(next) => onChange(next === "" ? null : (next as ProducerStanding))}
            active={only !== null}
          >
            <option value="">{t("producers.filter.any")}</option>
            {STANDINGS.map((standing) => (
              <option key={standing} value={standing}>
                {t("producers.filter.option", {
                  standing: t(`standing.${standing}`),
                  count: producers.filter((producer) => producer.standing === standing)
                    .length,
                })}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* The count and the way back out, as one sentence — the same pair the
          wine list draws under its bar, and silent when nothing is held back. */}
      <div className="mt-4 flex items-baseline gap-4">
        <p aria-live="polite" className="text-muted text-sm">
          {hiding
            ? t("producers.filter.count", {
                shown: shown.length,
                total: producers.length,
              })
            : t("producers.filter.total", { count: producers.length })}
        </p>
        {only !== null && (
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-accent text-sm underline-offset-4 hover:underline"
          >
            {t("producers.filter.clear")}
          </button>
        )}
      </div>

      {shown.length === 0 ? (
        <p className="text-muted mt-6 max-w-prose text-sm">
          {t("producers.filter.none")}
        </p>
      ) : (
        <ul className="border-line mt-4 border-t">{shown.map(children)}</ul>
      )}
    </>
  );
}
